import type { ReportRow } from '../../lib/types'

import { reportCard, reportCardPadSm, reportCardSub, reportCardTitle, reportEyebrow } from '../ui'



function num(v: unknown): number | null {

  if (typeof v === 'number' && Number.isFinite(v)) return v

  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)

  return null

}



function fmt(v: number | null | undefined): string {

  if (v == null) return '—'

  return Number.isInteger(v) ? String(v) : v.toFixed(1)

}



export function Scorecard({ rows }: { rows: ReportRow[] }) {

  const top = rows.slice(0, 8)

  return (

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">

      {top.map((r, i) => {

        const score = num(r.score)

        const name = String(r.driver_name ?? r.driver_id ?? '—')

        const bar = Math.max(0, Math.min(100, score ?? 0))

        return (

          <div key={i} className={`${reportCard} ${reportCardPadSm} transition-shadow duration-150 hover:shadow-md`}>

            <div className="flex items-baseline justify-between gap-2">

              <span className={reportEyebrow}>#{i + 1}</span>

              <span className="font-mono text-sm font-semibold tabular-nums text-slate-900">{fmt(score)}</span>

            </div>

            <div className={`mt-1 truncate ${reportCardTitle}`}>{name}</div>

            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">

              <div className="h-full origin-left animate-bar-in-x rounded-full bg-blue-600" style={{ width: `${bar}%`, animationDelay: `${i * 40}ms` }} />

            </div>

            <div className="mt-3 flex justify-between font-mono text-xs tabular-nums text-slate-500">

              <span>{fmt(num(r.km))} km</span>

              <span>{fmt(num(r.kwh_per_km))} kWh/km</span>

              <span>{fmt(num(r.harsh_events ?? r.harsh_brakes))} harsh</span>

            </div>

          </div>

        )

      })}

    </div>

  )

}



export function BreachCards({ rows }: { rows: ReportRow[] }) {

  const top = rows.slice(0, 6)

  return (

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">

      {top.map((r, i) => (

        <div key={i} className="rounded-xl border border-red-200/80 bg-red-50/50 p-4 shadow-sm ring-1 ring-red-900/5 transition-shadow duration-150 hover:shadow-md">

          <div className="flex items-center justify-between gap-2">

            <span className="font-mono text-xs font-semibold uppercase tracking-wide text-red-800">{String(r.kind ?? 'breach')}</span>

            <span className="font-mono text-xs text-slate-500">{String(r.hub_code ?? r.route_id ?? '')}</span>

          </div>

          <div className={`mt-1 ${reportCardTitle}`}>{String(r.vehicle_id ?? '—')}</div>

          <div className={`mt-1 ${reportCardSub}`}>

            actual {fmt(num(r.actual_min ?? r.tat_actual_min))} min · target{' '}

            {fmt(num(r.target_min ?? r.tat_target_min))} min

          </div>

          {r.likely_cause != null && (

            <div className="mt-3 inline-block rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">

              {String(r.likely_cause)}

            </div>

          )}

        </div>

      ))}

    </div>

  )

}



export function LeakageBars({ rows }: { rows: ReportRow[] }) {

  const top = rows.slice(0, 8)

  const max = Math.max(...top.map((r) => num(r.cost_inr) ?? 0), 1)

  return (

    <div className="space-y-3">

      {top.map((r, i) => {

        const cost = num(r.cost_inr) ?? 0

        const w = (cost / max) * 100

        return (

          <div key={i} className="flex items-center gap-3">

            <div className="w-40 truncate font-mono text-xs text-slate-500">{String(r.bucket ?? r.kind ?? 'leak')}</div>

            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">

              <div className="h-full origin-left animate-bar-in-x rounded-full bg-amber-500" style={{ width: `${w}%`, animationDelay: `${i * 40}ms` }} />

            </div>

            <div className="w-20 text-right font-mono text-xs font-semibold tabular-nums text-slate-900">₹{fmt(cost)}</div>

          </div>

        )

      })}

    </div>

  )

}



export function HotspotCards({ rows }: { rows: ReportRow[] }) {

  const top = rows.slice(0, 6)

  return (

    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">

      {top.map((r, i) => (

        <div key={i} className={`${reportCard} ${reportCardPadSm} transition-shadow duration-150 hover:shadow-md`}>

          <div className={reportEyebrow}>{String(r.hub_code ?? r.cell ?? r.zone ?? 'hotspot')}</div>

          <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{fmt(num(r.stops ?? r.count))} stops</div>

          <div className="mt-1 font-mono text-xs tabular-nums text-slate-500">

            {fmt(num(r.vehicles ?? r.vehicle_count))} vehicles ·{' '}

            {fmt(num(r.avg_min ?? r.avg_duration_min))} min avg

          </div>

        </div>

      ))}

    </div>

  )

}



export function FleetNowStrip({ rows }: { rows: ReportRow[] }) {

  const counts = new Map<string, number>()

  let laden = 0

  let empty = 0

  for (const r of rows) {

    const s = String(r.status ?? r.state ?? 'UNKNOWN')

    counts.set(s, (counts.get(s) ?? 0) + 1)

    const load = String(r.load_state ?? '').toUpperCase()

    if (load === 'LADEN') laden += 1

    else if (load === 'EMPTY') empty += 1

  }

  const entries = [...counts.entries()].sort((a, b) => b[1] - a[1])

  const pill = (label: string, value: number | string) => (

    <div className={`${reportCard} px-4 py-3`}>

      <div className={reportEyebrow}>{label}</div>

      <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{value}</div>

    </div>

  )

  return (

    <div className="flex flex-wrap gap-3">

      {pill('loaded · Dhar→Dhule', laden)}

      {pill('unloaded · Dhule→Dhar', empty)}

      {entries.map(([s, n]) => pill(s, n))}

      {pill('plates', rows.length)}

    </div>

  )

}



export function SpecializedViz({ reportId, rows }: { reportId: string; rows: ReportRow[] }) {

  switch (reportId) {

    case 'driver_scorecard': return <Scorecard rows={rows} />

    case 'tat_breach': return <BreachCards rows={rows} />

    case 'energy_leakage': return <LeakageBars rows={rows} />

    case 'unscheduled': return <HotspotCards rows={rows} />

    case 'fleet_now': return <FleetNowStrip rows={rows} />

    default: return null

  }

}


